import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useRollForward } from "@/hooks/useSubscriptionSchedules";
import type { RollForwardMonth } from "@/interfaces/subscriptionSchedules";
import {
  cell,
  money,
  num,
  periodLabel,
  SectionHeading,
  shortDay,
  tableBox,
  th,
  totalRow,
} from "./shared";

function lastDay(period: string) {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y as number, m as number, 0)).toISOString().slice(0, 10);
}

function StatusCell({ month }: { month: RollForwardMonth }) {
  const behind =
    month.status !== "upcoming" && month.status !== "in_progress" && Number(month.scheduled) !== 0;
  if (behind) {
    // A month that has ended with lines still unposted: recognition has not caught up.
    return (
      <Chip size="small" color="warning" variant="outlined" label="Behind · releases not posted" />
    );
  }
  switch (month.status) {
    case "closed":
      return <Chip size="small" label="Closed" />;
    case "open":
      return <Chip size="small" color="success" variant="outlined" label="Open" />;
    case "in_progress":
      return (
        <Chip
          size="small"
          variant="outlined"
          label={`In progress · releases ${shortDay(lastDay(month.period))}`}
          sx={{ borderColor: "var(--color-good-ochre)", color: "var(--color-good-ochre)" }}
        />
      );
    default:
      return <Box sx={{ fontSize: 12, color: "var(--color-graphite)" }}>Upcoming</Box>;
  }
}

/**
 * The fiscal year's 24100 roll-forward: one row per month, built from the schedules on the
 * ledger's dates. Each closing should equal the 24100 balance at that month end; the reconciliation
 * above shows any gap.
 */
export default function RollForwardTable({ fy }: { fy: number }) {
  const { data, isLoading } = useRollForward(fy);

  return (
    <Box component="section" aria-label="Year roll-forward" sx={{ mb: 4 }}>
      <SectionHeading
        title="Year roll-forward"
        note="Owed at start + paid − delivered = owed at end. Owed at end should match the books."
      />
      <Box sx={tableBox}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={th}>Month</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Owed at start</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Paid</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Delivered</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Owed at end</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Due to release</TableCell>
              <TableCell sx={th}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} sx={{ ...cell, color: "var(--color-graphite)" }}>
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {data?.months.map((m) => {
              const upcoming = m.opening === null;
              const current = m.status === "in_progress";
              const dim = upcoming ? { color: "var(--color-graphite)", fontStyle: "italic" } : {};
              return (
                <TableRow
                  key={m.period}
                  sx={
                    current ? { "& td": { background: "var(--color-good-ochre-bg)" } } : undefined
                  }
                >
                  <TableCell sx={{ ...cell, ...dim, fontWeight: current ? 700 : 400 }}>
                    {periodLabel(m.period)}
                  </TableCell>
                  <TableCell sx={{ ...num, ...dim }}>{upcoming ? "—" : money(m.opening)}</TableCell>
                  <TableCell sx={{ ...num, ...dim }}>{upcoming ? "—" : money(m.added)}</TableCell>
                  <TableCell sx={{ ...num, ...dim }}>
                    {upcoming ? "—" : money(m.recognised)}
                  </TableCell>
                  <TableCell sx={{ ...num, ...dim, fontWeight: upcoming ? 400 : 600 }}>
                    {upcoming ? "—" : money(m.closing)}
                  </TableCell>
                  <TableCell sx={{ ...num, ...dim }}>
                    {Number(m.scheduled) === 0 ? "—" : money(m.scheduled)}
                  </TableCell>
                  <TableCell sx={cell}>
                    <StatusCell month={m} />
                  </TableCell>
                </TableRow>
              );
            })}
            {data && (
              <TableRow sx={totalRow}>
                <TableCell sx={cell}>FY {data.fiscalYear}</TableCell>
                <TableCell sx={num}>{money(data.totals.opening)}</TableCell>
                <TableCell sx={num}>{money(data.totals.added)}</TableCell>
                <TableCell sx={num}>{money(data.totals.recognised)}</TableCell>
                <TableCell sx={num}>{money(data.totals.closing)}</TableCell>
                <TableCell sx={num}>{money(data.totals.scheduled)}</TableCell>
                <TableCell sx={cell} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
